import { OrgFullDetail, OrgMemberRole, OrgMemberWithUser } from "@garther/interfaces";
import { Button, Table } from "@garther/ui/components";
import { useI18n } from "@garther/ui/libs";
import { Clock, PenLine, Trash, Users } from "lucide-react";
import { useState } from "react";

import { useAuthStore } from "@/features/auth/auth.store";
import { useOrgPendingInvitationsQuery } from "@/features/org/hooks/use-org-query";

import { OrgMemberEditModal } from "./edit-modal";
import { OrgMemberInviteModal } from "./invite-modal";
import { OrgMemberRemoveModal } from "./remove-modal";

interface OrgMemberProps {
  org: OrgFullDetail;
}

function EmptyMembersState({ title, description }: { title: string; description: string }) {
  return (
    <div className="w-full py-12 border border-border rounded-lg bg-background-secondary/30">
      <div className="flex flex-col items-center justify-center text-center">
        <div className="mb-4 w-16 h-16 mx-auto bg-background-dark-secondary/10 rounded-full flex items-center justify-center">
          <Users className="w-8 h-8 text-text-secondary" strokeWidth={1.5} />
        </div>
        <div className="space-y-2">
          <p className="text-lg font-medium text-text-primary">{title}</p>
          <p className="text-sm text-text-secondary max-w-md">{description}</p>
        </div>
      </div>
    </div>
  );
}

function PendingBadge({ label }: { label: string }) {
  return (
    <span className="inline-flex items-center gap-1 text-xs text-yellow-600 bg-yellow-50 px-2 py-0.5 rounded-full">
      <Clock className="w-3 h-3" />
      {label}
    </span>
  );
}

const SHORT_DATE: Intl.DateTimeFormatOptions = {
  year: "numeric",
  month: "short",
  day: "numeric"
};

export function OrgMember({ org }: OrgMemberProps) {
  const { formatDate, t } = useI18n();
  const { curUser } = useAuthStore();

  const isOwner = org.members.some(
    (member) => member.userId === curUser?.profile?.id && member.role === OrgMemberRole.OWNER
  );

  const { data: pendingInvitations = [] } = useOrgPendingInvitationsQuery(isOwner ? org.id : "");

  const [isInviteOpen, setIsInviteOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<{
    userId: string;
    name: string;
    role: OrgMemberRole;
  } | null>(null);
  const [removeTarget, setRemoveTarget] = useState<{
    userId: string;
    name: string;
  } | null>(null);

  const actionsColumn = {
    key: "actions",
    title: t("org.members.table.actions"),
    render: (_value: unknown, record: OrgMemberWithUser) => {
      const isSelf = record.userId === curUser?.profile?.id;
      return (
        <div className="flex gap-1">
          <Button
            variant="ghost"
            padding="p-2"
            onClick={() =>
              setEditTarget({
                userId: record.userId,
                name: record.user.name,
                role: record.role
              })
            }
          >
            <PenLine className="w-4 h-4" />
          </Button>
          {!isSelf && (
            <Button
              variant="ghost"
              padding="p-2"
              className="hover:text-red-500"
              onClick={() =>
                setRemoveTarget({
                  userId: record.userId,
                  name: record.user.name
                })
              }
            >
              <Trash className="w-4 h-4" />
            </Button>
          )}
        </div>
      );
    }
  };

  const memberColumns = [
    { key: "name", title: t("org.members.table.name"), dataIndex: "user.name" },
    { key: "role", title: t("org.members.table.role"), dataIndex: "role" },
    {
      key: "email",
      title: t("org.members.table.email"),
      dataIndex: "user.email"
    },
    {
      key: "phone",
      title: t("org.members.table.phone"),
      dataIndex: "user.phone"
    },
    {
      key: "lastUpdate",
      title: t("org.members.table.lastUpdatedAt"),
      dataIndex: "updatedAt",
      formatter: (value: Date) => formatDate(value, SHORT_DATE)
    },
    ...(isOwner ? [actionsColumn] : [])
  ];

  const pendingColumns = [
    { key: "email", title: t("org.members.table.email"), dataIndex: "email" },
    { key: "role", title: t("org.members.table.role"), dataIndex: "role" },
    {
      key: "expiresAt",
      title: t("org.members.pendingInvitations.expiresAt"),
      dataIndex: "expiresAt",
      formatter: (value: Date) => formatDate(value, SHORT_DATE)
    },
    {
      key: "status",
      title: t("org.members.table.status"),
      render: () => <PendingBadge label={t("org.members.pendingInvitations.pending")} />
    }
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-responsive-xl font-light text-text-primary uppercase font-literata mb-1">
            {t("org.members.title")}
          </p>
          <p className="text-responsive-base font-normal text-text-secondary">{t("org.members.subtitle")}</p>
        </div>
        {isOwner && (
          <Button variant="outline" padding="px-4 py-2" onClick={() => setIsInviteOpen(true)}>
            {t("org.members.inviteMember")}
          </Button>
        )}
      </div>

      {org.members.length === 0 ? (
        <EmptyMembersState
          title={t("org.members.empty.title")}
          description={t("org.members.empty.description")}
        />
      ) : (
        <div className="w-full space-y-4 border border-border-light rounded-xl">
          <Table data={org.members} className="rounded-xl bg-background-secondary" columns={memberColumns} />
        </div>
      )}

      {isOwner && pendingInvitations.length > 0 && (
        <div className="space-y-3">
          <div>
            <p className="text-responsive-base font-medium text-text-primary">
              {t("org.members.pendingInvitations.title")}
            </p>
            <p className="text-sm text-text-secondary">{t("org.members.pendingInvitations.subtitle")}</p>
          </div>
          <Table data={pendingInvitations} columns={pendingColumns} />
        </div>
      )}

      <OrgMemberInviteModal orgId={org.id} isOpen={isInviteOpen} onClose={() => setIsInviteOpen(false)} />
      <OrgMemberEditModal orgId={org.id} member={editTarget} onClose={() => setEditTarget(null)} />
      <OrgMemberRemoveModal orgId={org.id} member={removeTarget} onClose={() => setRemoveTarget(null)} />
    </div>
  );
}
